import {
  createAction,
  createAsyncThunk,
  createEntityAdapter,
  createReducer,
  createSelector,
  EntityState,
} from "@reduxjs/toolkit";
import { RootState } from "store/types";
import { IAccessRequestEntity } from "models/AccessRequest.model";
import { serviceContainer } from "services/serviceContainer";

export type IAccessRequestState = {
  incomingAccessRequests: EntityState<IAccessRequestEntity>;
  myAccessRequests: EntityState<IAccessRequestEntity>;
};

export const generateStorageKey = (accessRequestGuid: string) => {
  const prefix = "Objective.Build.MyAccessRequestAccordion.AccessRequestOpened";
  return `${prefix}.${accessRequestGuid}`;
};

const accessRequestEntityAdapter = createEntityAdapter<IAccessRequestEntity>({
  selectId: (entity) => entity.requestGuid,
  sortComparer: (a, b) => {
    return b.requestGuid.localeCompare(a.requestGuid);
  },
});

export const loadIncomingAccessRequests = createAction<IAccessRequestEntity[]>(
  "domainData/accessRequest/loadIncomingAccessRequests"
);
export const loadMyAccessRequests = createAction<IAccessRequestEntity[]>(
  "domainData/accessRequest/loadMyAccessRequests"
);

export const fetchAccessRequests = createAsyncThunk(
  "domainData/accessRequest/fetchAccessRequests",
  async (_, thunkAPI) => {
    const {
      incomingAccessRequests,
      myAccessRequests,
    } = await serviceContainer.cradle.accessRequestService.fetchAccessRequests();

    thunkAPI.dispatch(loadIncomingAccessRequests(incomingAccessRequests));
    thunkAPI.dispatch(loadMyAccessRequests(myAccessRequests));

    return { incomingAccessRequests, myAccessRequests };
  }
);

export const rejectAccessRequest = createAsyncThunk(
  "domainData/accessRequest/rejectAccessRequest",
  async (requestGuid: string) => {
    await serviceContainer.cradle.accessRequestService.respondToApplicationAccessRequest({
      requestGuid,
      response: "DENIED",
    });
    return requestGuid;
  }
);

export const completeAccessRequest = createAsyncThunk(
  "domainData/accessRequest/completeAccessRequest",
  (requestGuid: string) => requestGuid
);

export const requestApplicationAccess = createAsyncThunk(
  "domainData/accessRequest/requestApplicationAccessRequest",
  async (applicationId: number) => {
    await serviceContainer.cradle.accessRequestService.requestApplicationAccess(applicationId);
  }
);

export const initialState: IAccessRequestState = {
  incomingAccessRequests: accessRequestEntityAdapter.getInitialState(),
  myAccessRequests: accessRequestEntityAdapter.getInitialState(),
};

export const accessRequestReducer = createReducer<typeof initialState>(initialState, (builder) =>
  builder
    .addCase(loadIncomingAccessRequests, (state, entities) => {
      accessRequestEntityAdapter.setAll(state.incomingAccessRequests, entities);
    })
    .addCase(loadMyAccessRequests, (state, entities) => {
      accessRequestEntityAdapter.setAll(state.myAccessRequests, entities);
    })
    .addCase(rejectAccessRequest.fulfilled, (state, entities) => {
      accessRequestEntityAdapter.removeOne(state.incomingAccessRequests, entities);
      accessRequestEntityAdapter.removeOne(state.myAccessRequests, entities);
    })
    .addCase(completeAccessRequest.fulfilled, (state, entities) => {
      accessRequestEntityAdapter.removeOne(state.incomingAccessRequests, entities);
      accessRequestEntityAdapter.removeOne(state.myAccessRequests, entities);
    })
);

export const {
  selectById: selectIncomingAccessRequestEntityById,
  selectAll: selectAllIncomingAccessRequestEntities,
} = accessRequestEntityAdapter.getSelectors(
  (state: RootState) => state.domainData.accessRequests.incomingAccessRequests
);

export const {
  selectById: selectMyAccessRequestEntityById,
  selectAll: selectAllMyAccessRequestEntities,
} = accessRequestEntityAdapter.getSelectors((state: RootState) => state.domainData.accessRequests.myAccessRequests);

export const selectRecentMyAccessRequests = createSelector([selectAllMyAccessRequestEntities], (myAccessRequests) => {
  return myAccessRequests.filter(
    (accessRequest) => localStorage.getItem(generateStorageKey(accessRequest.requestGuid)) !== "true"
  );
});
